/**
 * RunCap Page — Runway Capacity Analysis Control Room
 *
 * Two views:
 *   (a) Project listing — cards showing RunCap status per project
 *   (b) Detail — Three-layer calculation engine:
 *       Layer 1: Baseline (IATA ADRM / FAA / SAM)
 *       Layer 2: DACAMS Dynamic Trims (Congestion + WCF)
 *       Layer 3: Tri-Node Equilibrium (CC/BCI/SVS/RGPP)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { projectsService, Project } from '../services/projects';
import { runcapService } from '../services/runcapService';
import type {
    RunwayConfig,
    RunwayCapacityResult,
    TriNodeResult,
    CalculationMethod,
    WeatherCategory,
    RunwayMode,
    SeparationStandard,
    AircraftMix,
} from '../types/runcap';
import {
    DEFAULT_RUNWAY_CONFIG,
    WEATHER_LABELS,
    MODE_LABELS,
    RECAT_EU_LABELS,
    METHOD_LABELS,
} from '../types/runcap';
import {
    Plane,
    Gauge,
    AlertTriangle,
    Activity,
    Zap,
    Settings2,
    ChevronDown,
    ChevronUp,
    Loader2,
    ArrowRight,
    ArrowLeft,
    BarChart3,
    Building2,
    Radio,
    Search,
    ListFilter,
    Calendar,
    CheckCircle2,
    Clock,
} from 'lucide-react';

// ==================== COMPONENT ====================

const RunCapPage: React.FC = () => {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const isDark = theme === 'dark';
    const [searchParams] = useSearchParams();

    // View state
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
        searchParams.get('projectId')
    );

    // Project listing state
    const [projects, setProjects] = useState<Project[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<'date' | 'name'>('date');

    // Analysis state
    const [config, setConfig] = useState<RunwayConfig>({ ...DEFAULT_RUNWAY_CONFIG });
    const [projectRunways, setProjectRunways] = useState<any[]>([]);   // all runcap_infra entries
    const [selectedRunwayIdx, setSelectedRunwayIdx] = useState<number>(0);
    const [result, setResult] = useState<RunwayCapacityResult | null>(null);
    const [triNode, setTriNode] = useState<TriNodeResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Layer 2 inputs — all DACAMS-derived and read-only except betaW (model sensitivity knob)
    const [congestion, setCongestion] = useState(0);
    const [wcfScore, setWcfScore] = useState(0);
    const [betaW, setBetaW] = useState(0.3);

    // Layer 3 inputs — DACAMS-derived and read-only
    const [racEff, setRacEff] = useState(40);
    const [ptbc, setPtbc] = useState(50);
    const [demand, setDemand] = useState(35);

    // Aircraft Mix (RECAT-EU — wake) automatic from apcas_blocks.block7.rrc_fleet_mix.
    // null = no DACAMS mix key found for this project (honesty rule: never fabricate one).
    const [dacamsFleetMix, setDacamsFleetMix] = useState<AircraftMix | null>(null);
    // Glass box for that mix: {total, classified, unknown, unknown_share, unknown_types, source}
    const [dacamsFleetMixStats, setDacamsFleetMixStats] = useState<any | null>(null);

    // The project's ONE runway capacity, as resolved by the DACAMS tri-node (block 7).
    // System-level (all runways together) — never summed with anything else.
    const [dacamsRrc, setDacamsRrc] = useState<{
        rrc: number;
        source: string;
        detail: string;
        method: string;
        breakdown: any | null;
        notes: string[];
    } | null>(null);

    // Multi-runway results (one per projectRunways entry)
    const [runwayResults, setRunwayResults] = useState<Array<RunwayCapacityResult | null>>([]);

    // Save-to-project state
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Track which Layer 2 / Tri-Node values were loaded from DACAMS analysis_results
    // vs. manually edited by the user
    const [dacamsOrigins, setDacamsOrigins] = useState<Set<string>>(new Set());
    const markDacams = (fields: string[]) =>
        setDacamsOrigins(prev => new Set([...prev, ...fields]));
    const markManual = (field: string) =>
        setDacamsOrigins(prev => { const s = new Set(prev); s.delete(field); return s; });

    // Theme classes
    const textMain = 'text-[var(--text-primary)]';
    const textSub = 'text-[var(--text-secondary)]';
    const cardBg = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-[var(--border)] shadow-sm hover:shadow-md';

    // Detail view theme
    const bg = isDark ? 'bg-[#0B1120]' : 'bg-gray-50';
    const card = isDark ? 'bg-[#111827]/90 border-[var(--border)]' : 'bg-white border-[var(--border)]';
    const text = isDark ? 'text-gray-100' : 'text-gray-900';
    const textSubDetail = 'text-[var(--text-secondary)]';
    const accent = 'text-[#F26430]';
    const selectBg = isDark ? 'bg-[#1E293B] text-gray-200 border-[var(--border)]' : 'bg-white text-gray-800 border-gray-300';

    // Load projects on mount
    useEffect(() => {
        loadProjects();
    }, []);

    const loadProjects = async () => {
        try {
            const data = await projectsService.getAll();
            setProjects(data);
        } catch (err) {
            console.error('Error loading projects:', err);
        } finally {
            setLoadingProjects(false);
        }
    };

    // Helper: apply one runcap_infra entry to config
    const applyRunwayEntry = useCallback((rw: any) => {
        setConfig(prev => ({
            ...prev,
            runway_id: rw.runway_id || prev.runway_id,
            length_m: rw.length_m || prev.length_m,
            n_rets: rw.rapid_exit_taxiways ?? 0,
            n_holding_points: rw.holding_points ?? 1,
            ils_category: rw.ils_category || '',
            rot_arrival_s: rw.rot_arrival_s || prev.rot_arrival_s,
            rot_departure_s: rw.rot_departure_s || prev.rot_departure_s,
            rapid_exit_taxiways: rw.rapid_exit_taxiways ?? 0,
            holding_points: rw.holding_points ?? 1,
        }));
    }, []);

    // Load project infra when selected — store all runways, pre-populate Layer 2 + Tri-Node
    useEffect(() => {
        if (!selectedProjectId) return;
        const loadProject = async () => {
            try {
                const proj = await projectsService.getOne(selectedProjectId);
                if (!proj) return;
                const sme = proj.sme_parameters || {};
                const infra = sme.runcap_infra;

                // Pre-populate from DACAMS analysis_results
                const ar = (proj as any).analysis_results || {};
                // Backward-compat: legacy projects persisted the acas_blocks key.
                const apcasBlocks = ar.apcas_blocks || ar.acas_blocks || {};
                const block2 = apcasBlocks.block2 || {};
                const block4 = apcasBlocks.block4 || {};
                const block7 = apcasBlocks.block7 || {};
                const kpis = ar.kpis || {};
                const ci = ar.capacity_integration || {};

                // --- Load runway infra ---
                // Primary: sme_parameters.runcap_infra
                // Fallback: designators seen in the AODB (capacity_integration.runway_stats).
                // NOTE: capacity_integration no longer carries a runcap block — the runway
                // node has a single source, apcas_blocks.block7 (see below).
                let runwayEntries = Array.isArray(infra) && infra.length > 0 ? infra : [];
                if (runwayEntries.length === 0 && ci.runway_stats?.runways_used?.length > 0) {
                    runwayEntries = ci.runway_stats.runways_used.map((rid: string) => ({
                        runway_id: rid,
                        length_m: 3000,
                        surface: 'ASP',
                        ils_category: '',
                        rapid_exit_taxiways: 0,
                        holding_points: 1,
                    }));
                }
                if (runwayEntries.length > 0) {
                    setProjectRunways(runwayEntries);
                    setSelectedRunwayIdx(0);
                    applyRunwayEntry(runwayEntries[0]);
                } else {
                    setProjectRunways([]);
                }

                // --- DACAMS runway node (SINGLE SOURCE) -----------------------------
                // apcas_blocks.block7 is the only place a runway capacity for this
                // project exists. It is the RUNWAY SYSTEM figure (both runways when the
                // airport runs dependent parallels), not a per-runway value, so it is
                // never summed across runways. The page's own calculator below stays a
                // what-if tool and does not overwrite it.
                setRunwayResults(runwayEntries.map(() => null));
                setDacamsRrc(
                    typeof block7.rrc_input === 'number'
                        ? {
                              rrc: block7.rrc_input,
                              source: block7.rrc_source ?? 'unknown',
                              detail: block7.rrc_detail ?? '',
                              method: block7.runcap_method ?? '',
                              breakdown: block7.rrc_breakdown ?? null,
                              notes: Array.isArray(block7.rrc_notes) ? block7.rrc_notes : [],
                          }
                        : null,
                );

                // Reset origins for the new project
                setDacamsOrigins(new Set());
                const loaded: string[] = [];

                // Aircraft Mix (RECAT-EU — wake turbulence) from the DACAMS datasheet.
                // Real key: apcas_blocks.block7.rrc_fleet_mix — the mix actually fed to
                // RunCap, classified with the canonical EASA/EUROCONTROL RECAT-EU table
                // (backend/app/domain/runcap/recat.py). An A320 is RECAT D here; its
                // ICAO ARC letter (C) belongs to the apron node and is NOT interchangeable.
                // Never user-editable, never fabricated: absent key -> null, UI shows "sin dato".
                setDacamsFleetMixStats(block7.rrc_fleet_mix_stats ?? null);
                const fleetMixRaw = block7.rrc_fleet_mix;
                if (fleetMixRaw && typeof fleetMixRaw === 'object' && Object.keys(fleetMixRaw).length > 0) {
                    const dacamsMix: AircraftMix = {
                        cat_a: fleetMixRaw.cat_a ?? 0,
                        cat_b: fleetMixRaw.cat_b ?? 0,
                        cat_c: fleetMixRaw.cat_c ?? 0,
                        cat_d: fleetMixRaw.cat_d ?? 0,
                        cat_e: fleetMixRaw.cat_e ?? 0,
                        cat_f: fleetMixRaw.cat_f ?? 0,
                    };
                    setDacamsFleetMix(dacamsMix);
                    setConfig(prev => ({ ...prev, aircraft_mix: dacamsMix }));
                    loaded.push('fleetMix');
                } else {
                    setDacamsFleetMix(null);
                }

                // WCF Score — Weather Complexity Factor from METAR (Block 2)
                const wcf = block2.wcf ?? block2.wcf_meteo ?? 0;
                if (wcf > 0) { setWcfScore(+wcf.toFixed(3)); loaded.push('wcf'); }

                // Apron Back-Pressure (f_apron_congestion) — derived from NSS (Block 5)
                // NSS = PHT/RAC — apron saturation score. If NSS > 0.7, apron starts generating
                // back-pressure on runway throughput (scale 0 → 0.25 linearly over NSS 0.7→1.2)
                const nss = (ar.apcas_blocks || ar.acas_blocks)?.block5?.nss_nominal ?? 0;
                if (nss > 0.7) {
                    const fCong = Math.min(0.25, Math.max(0, (nss - 0.7) / (1.2 - 0.7) * 0.25));
                    setCongestion(+fCong.toFixed(3));
                    loaded.push('congestion');
                }

                // RAC_eff from Block 4 (DACAMS APCAS output)
                const rac = block4.rac_capacity ?? kpis.rac_capacity ?? kpis.rac ?? 0;
                if (rac > 0) { setRacEff(Math.round(rac)); loaded.push('racEff'); }

                // PTBC from AirCap or sme_parameters
                const ptbcVal =
                    ci.aircap?.practical_capacity ??
                    ci.aircap?.total_capacity ??
                    sme.ptbc_capacity ?? 0;
                if (ptbcVal > 0) { setPtbc(Math.round(ptbcVal)); loaded.push('ptbc'); }

                // Peak demand from demand_curve or KPIs
                const demandVal =
                    ar.demand_curve?.peak_hour_demand ??
                    kpis.pht_horario_realista ??
                    kpis.peak_hour_traffic ?? 0;
                if (demandVal > 0) { setDemand(Math.round(demandVal)); loaded.push('demand'); }

                // ARR/DEP ratio from AODB movement distribution
                const movDist = ar.advanced_analysis?.charts?.departure_distribution;
                const totalArr = movDist?.total_arrivals ?? 0;
                const totalDep = movDist?.total_departures ?? 0;
                const totalMov = totalArr + totalDep;
                if (totalMov > 0) {
                    const arrRatio = +(totalArr / totalMov).toFixed(3);
                    updateConfig({ arr_dep_ratio: arrRatio });
                    loaded.push('arrDepRatio');
                }

                if (loaded.length > 0) markDacams(loaded);

            } catch { /* ignore */ }
        };
        loadProject();
    }, [selectedProjectId, applyRunwayEntry]);

    const updateConfig = useCallback((updates: Partial<RunwayConfig>) => {
        setConfig(prev => ({ ...prev, ...updates }));
    }, []);

    const handleCalculate = async () => {
        setLoading(true);
        setError('');
        try {
            if (projectRunways.length > 1) {
                // Multi-runway mode: calculate all simultaneously
                const newResults = await Promise.all(
                    projectRunways.map(async (rw) => {
                        const rwConfig: RunwayConfig = {
                            ...config,
                            runway_id: rw.runway_id || config.runway_id,
                            length_m: rw.length_m || config.length_m,
                            n_rets: rw.rapid_exit_taxiways ?? 0,
                            n_holding_points: rw.holding_points ?? 1,
                            ils_category: rw.ils_category || '',
                            rot_arrival_s: rw.rot_arrival_s || config.rot_arrival_s,
                            rot_departure_s: rw.rot_departure_s || config.rot_departure_s,
                            rapid_exit_taxiways: rw.rapid_exit_taxiways ?? 0,
                            holding_points: rw.holding_points ?? 1,
                        };
                        return runcapService.fullAnalysis(rwConfig, congestion, wcfScore, betaW);
                    })
                );
                setRunwayResults(newResults);
                // Tri-Node: bottleneck is the minimum RRC across all physical runways
                const minRRC = Math.min(...newResults.map(r => r?.dacams_rrc ?? Infinity));
                if (isFinite(minRRC) && minRRC > 0) {
                    const tn = await runcapService.triNode(minRRC, racEff, ptbc, demand);
                    setTriNode(tn);
                }
            } else {
                // Single-runway mode
                const res = await runcapService.fullAnalysis(config, congestion, wcfScore, betaW);
                setResult(res);
                if (res.dacams_rrc && res.dacams_rrc > 0) {
                    const tn = await runcapService.triNode(res.dacams_rrc, racEff, ptbc, demand);
                    setTriNode(tn);
                }
            }
        } catch (e: any) {
            setError(e.response?.data?.detail || e.message || 'Calculation error');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToProjects = () => {
        setSelectedProjectId(null);
        setResult(null);
        setRunwayResults([]);
        setTriNode(null);
        setError('');
        setConfig({ ...DEFAULT_RUNWAY_CONFIG });
        setDacamsOrigins(new Set());
        setDacamsFleetMix(null);
        setDacamsFleetMixStats(null);
        setDacamsRrc(null);
    };

    const handleSaveToProject = async () => {
        if (!selectedProjectId) return;
        setSaving(true);
        setSaveSuccess(false);
        try {
            if (projectRunways.length > 1) {
                const validResults = runwayResults.filter(r => r !== null) as RunwayCapacityResult[];
                const rrcTotal = validResults.reduce((sum, r) => sum + (r.dacams_rrc ?? 0), 0);
                const perRunway = validResults.map(r => ({
                    runway_id: r.runway_id,
                    rrc: r.dacams_rrc ?? 0,
                    max_arrivals: r.max_arrivals_per_hour,
                    max_departures: r.max_departures_per_hour,
                }));
                await runcapService.saveToProject(selectedProjectId, rrcTotal, perRunway);
            } else if (result) {
                await runcapService.saveToProject(selectedProjectId, result.dacams_rrc ?? 0, [{
                    runway_id: result.runway_id,
                    rrc: result.dacams_rrc ?? 0,
                    max_arrivals: result.max_arrivals_per_hour,
                    max_departures: result.max_departures_per_hour,
                }]);
            }
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch {
            // button returns to normal state
        } finally {
            setSaving(false);
        }
    };

    // DACAMS AM calculation
    const mix = config.aircraft_mix;
    const dacamsAM = (
        (mix.cat_f / 100) * 0.8 +
        ((mix.cat_d + mix.cat_e) / 100) * 1.0 +
        ((mix.cat_b + mix.cat_c) / 100) * 1.5 +
        (mix.cat_a / 100) * 2.5
    ).toFixed(3);

    // FAA Mix Index
    const faaMI = (mix.cat_b + mix.cat_c + 3 * mix.cat_a).toFixed(1);

    // Filter & sort projects
    const processedProjects = projects
        .filter(p => {
            const search = searchTerm.toLowerCase();
            return (
                p.name.toLowerCase().includes(search) ||
                p.iata_code?.toLowerCase().includes(search) ||
                p.icao_code?.toLowerCase().includes(search)
            );
        })
        .sort((a, b) => {
            if (sortBy === 'date') return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
            return a.name.localeCompare(b.name);
        });

    // Derive RunCap status
    const getRunCapStatus = (project: Project): { label: string; color: string; icon: React.ReactNode } => {
        const hasRunCapData = project.analysis_results?.runcap || (project.runway_capacity && project.runway_capacity > 0);
        if (hasRunCapData) {
            return {
                label: language === 'es' ? 'ANALIZADO' : 'ANALYZED',
                color: 'text-green-500',
                icon: <CheckCircle2 size={14} />,
            };
        }
        return {
            label: language === 'es' ? 'PENDIENTE' : 'PENDING',
            color: 'text-yellow-500',
            icon: <Clock size={14} />,
        };
    };

    // ===================== VIEW: PROJECT LISTING =====================
    if (!selectedProjectId) {
        return (
            <div className="p-8 font-sans relative min-h-full">
                <div className="max-w-7xl mx-auto">

                    {/* HEADER */}
                    <div className={`flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 border-b pb-8 ${isDark ? 'border-[#2a2a2a]' : 'border-gray-300'}`}>
                        <div>
                            <h1 className={`text-4xl font-extrabold flex items-center gap-3 ${textMain}`}>
                                <Plane className="w-8 h-8 text-[#F26430]" />
                                RunCap
                            </h1>
                            <p className={`text-sm mt-2 tracking-wide font-medium ${textSub}`}>
                                {language === 'es'
                                    ? 'Capacidad de Pista · Motor de tres capas · DACAMS v5.0'
                                    : 'Runway Capacity · Three-layer engine · DACAMS v5.0'}
                            </p>
                        </div>
                    </div>

                    {/* SEARCH & SORT */}
                    <div className="flex flex-col md:flex-row gap-4 mb-8 max-w-4xl">
                        <div className={`flex-1 p-1 rounded-xl shadow-sm border flex items-center ${cardBg}`}>
                            <div className="pl-4 pr-2 text-[var(--text-secondary)]"><Search className="h-5 w-5" /></div>
                            <input
                                type="text"
                                placeholder={language === 'es' ? 'Buscar por nombre, código IATA o OACI...' : 'Search by name, IATA or ICAO code...'}
                                className={`w-full py-2 bg-transparent placeholder-gray-400 focus:outline-none text-sm ${textMain}`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className={`w-full md:w-64 p-1 rounded-xl shadow-sm border flex items-center px-4 ${cardBg}`}>
                            <ListFilter className="w-4 h-4 text-[#F26430] mr-2" />
                            <span className={`text-xs font-bold uppercase mr-2 ${textSub}`}>{language === 'es' ? 'ORDENAR' : 'SORT'}:</span>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as any)}
                                className={`bg-transparent outline-none text-sm font-bold cursor-pointer ${textMain} w-full`}
                            >
                                <option value="date" className="text-black">{language === 'es' ? 'Última Modif.' : 'Last Modified'}</option>
                                <option value="name" className="text-black">{language === 'es' ? 'Nombre (A-Z)' : 'Name (A-Z)'}</option>
                            </select>
                        </div>
                    </div>

                    {/* PROJECT GRID */}
                    {loadingProjects ? (
                        <div className="flex justify-center py-32"><div className="animate-spin rounded-full h-12 w-12 border-t-4 border-[#F26430]"></div></div>
                    ) : processedProjects.length === 0 ? (
                        <div className={`flex flex-col items-center justify-center py-24 rounded-2xl border-2 border-dashed ${isDark ? 'border-[var(--border)] bg-[var(--surface-raised)]0' : 'border-gray-300 bg-gray-50'}`}>
                            <Plane className="h-12 w-12 text-[var(--text-secondary)] mb-4 transform -rotate-45" />
                            <p className={textSub}>{language === 'es' ? 'No hay proyectos. Crea uno desde el HUB DACAMS.' : 'No projects. Create one from the DACAMS HUB.'}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                            {processedProjects.map((project) => {
                                const status = getRunCapStatus(project);
                                return (
                                    <div
                                        key={project.id}
                                        onClick={() => setSelectedProjectId(project.id)}
                                        className={`group rounded-xl border shadow-lg hover:shadow-2xl transition-all cursor-pointer overflow-hidden relative flex flex-col ${cardBg} hover:border-[#F26430]/50`}
                                    >
                                        <div className={`h-1 w-full ${status.color === 'text-green-500' ? 'bg-green-500' : 'bg-yellow-500'}`} />

                                        <div className="p-6 flex-1">
                                            <div className="flex items-center gap-4 mb-4">
                                                <div className={`h-12 w-12 rounded-lg flex items-center justify-center font-mono font-bold text-lg shadow-inner ${isDark ? 'bg-[var(--surface-raised)] text-[var(--text-primary)] border border-[var(--border)]' : 'bg-gray-100 text-[#1C1C1C] border border-[var(--border)]'}`}>
                                                    {project.iata_code || <Plane size={20} />}
                                                </div>
                                                <div>
                                                    <h3 className={`font-bold text-lg line-clamp-1 ${textMain}`}>{project.name}</h3>
                                                    <p className="text-xs font-mono text-[var(--text-tertiary)] bg-opacity-10 bg-gray-500 px-2 py-0.5 rounded inline-block mt-1">
                                                        {project.icao_code || '----'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="space-y-2 mt-4 pt-4 border-t border-gray-500/20">
                                                <div className="flex justify-between text-xs">
                                                    <span className={`flex items-center gap-2 ${textSub}`}><Calendar size={14} /> {language === 'es' ? 'Creado' : 'Created'}</span>
                                                    <span className={`font-mono ${textMain}`}>{project.created_at ? new Date(project.created_at).toLocaleDateString() : '-'}</span>
                                                </div>
                                                <div className="flex justify-between text-xs">
                                                    <span className={`flex items-center gap-2 ${textSub}`}><Gauge size={14} /> RunCap</span>
                                                    <span className={`font-bold flex items-center gap-1 ${status.color}`}>
                                                        {status.icon} {status.label}
                                                    </span>
                                                </div>
                                                {project.runway_capacity && project.runway_capacity > 0 && (
                                                    <div className="flex justify-between text-xs">
                                                        <span className={`flex items-center gap-2 ${textSub}`}><Activity size={14} /> RRC</span>
                                                        <span className={`font-mono font-bold ${accent}`}>{project.runway_capacity} mov/h</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className={`px-6 py-3 border-t flex justify-between items-center group-hover:bg-blue-500/5 transition-colors ${isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-gray-50 border-[var(--border)]'}`}>
                                            <span className={`text-[10px] font-bold uppercase tracking-widest ${textSub}`}>{language === 'es' ? 'Abrir RunCap' : 'Open RunCap'}</span>
                                            <ArrowRight className="h-4 w-4 text-[#F26430] transform group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ===================== VIEW: ANALYSIS DETAIL =====================
    const selectedProject = projects.find(p => p.id === selectedProjectId);

    return (
        <div className={`min-h-screen p-6 font-sans transition-colors duration-300 ${bg}`}>
            <div className="max-w-[1600px] mx-auto">

                {/* ── HEADER ────────────────────── */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleBackToProjects}
                            className={`p-2 rounded-lg border transition-colors ${isDark ? 'border-[var(--border)] hover:bg-[var(--surface-raised)]' : 'border-gray-300 hover:bg-gray-100'}`}
                        >
                            <ArrowLeft className="w-5 h-5 text-[#F26430]" />
                        </button>
                        <div className="p-2 bg-gradient-to-br from-[#F26430] to-[#e04f1a] rounded-xl shadow-lg text-white">
                            <Plane className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className={`text-2xl font-bold ${text}`}>
                                RunCap — Runway Capacity
                                {selectedProject && (
                                    <span className={`text-base font-normal ml-2 ${textSubDetail}`}>
                                        — {selectedProject.iata_code ? `[${selectedProject.iata_code}] ` : ''}{selectedProject.name}
                                    </span>
                                )}
                            </h1>
                            <p className={`text-sm ${textSubDetail}`}>
                                {language === 'es'
                                    ? 'Motor de tres capas · DACAMS v5.0'
                                    : 'Three-layer capacity engine · DACAMS v5.0'}
                            </p>
                        </div>
                    </div>
                    {/* Method selector */}
                    <div className="flex items-center gap-3">
                        <label className={`text-sm font-medium ${textSubDetail}`}>Engine:</label>
                        <select
                            value={config.method}
                            onChange={e => updateConfig({ method: e.target.value as CalculationMethod })}
                            className={`px-3 py-2 rounded-lg border text-sm font-medium ${selectBg}`}
                        >
                            {Object.entries(METHOD_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>{v.name}</option>
                            ))}
                        </select>
                        <button
                            onClick={handleCalculate}
                            disabled={loading}
                            className="px-5 py-2 rounded-lg bg-gradient-to-r from-[#F26430] to-[#e04f1a] text-white font-bold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center gap-2"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                            {loading ? (language === 'es' ? 'Calculando...' : 'Calculating...') : (language === 'es' ? 'Analizar' : 'Analyze')}
                        </button>
                        {(runwayResults.some(r => r !== null) || result) && selectedProjectId && (
                            <button
                                onClick={handleSaveToProject}
                                disabled={saving}
                                className={`px-4 py-2 rounded-lg border font-bold text-sm flex items-center gap-2 transition-all ${
                                    saveSuccess
                                        ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10'
                                        : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[#F26430]/50 hover:text-[#F26430]'
                                }`}
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saveSuccess ? <CheckCircle2 className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                                {saveSuccess ? (language === 'es' ? 'Guardado' : 'Saved') : (language === 'es' ? 'Guardar RRC' : 'Save RRC')}
                            </button>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-500/50 text-red-300 text-sm flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" /> {error}
                    </div>
                )}

                {/* ── The project's ONE runway capacity (tri-node, block 7) ────────── */}
                <DacamsRrcCard
                    data={dacamsRrc}
                    card={card}
                    text={text}
                    accent={accent}
                    textSubDetail={textSubDetail}
                    language={language}
                />

                {/* ── TOP: Shared Inputs ────────────── */}
                {projectRunways.length > 1 ? (
                    /* ── MULTI-RUNWAY MODE: 3 shared panels ── */
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">

                        {/* Shared Operational Config */}
                        <div className={`p-5 rounded-xl border ${card}`}>
                            <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${accent}`}>
                                <Settings2 className="w-4 h-4" /> Shared Config
                            </h3>
                            <div className="space-y-2">
                                <SelectRow label="Mode" value={config.mode} options={MODE_LABELS} onChange={v => updateConfig({ mode: v as RunwayMode })} isDark={isDark} />
                                <SelectRow label="Weather" value={config.weather} options={WEATHER_LABELS} onChange={v => updateConfig({ weather: v as WeatherCategory })} isDark={isDark} />
                                <SelectRow label="Separation" value={config.separation_std} options={{ 'ICAO': 'ICAO', 'RECAT-EU': 'RECAT-EU', 'RECAT-PWS': 'RECAT-PWS' }} onChange={v => updateConfig({ separation_std: v as SeparationStandard })} isDark={isDark} />
                                <SourceInputRow
                                    label="% Llegadas"
                                    value={Math.round(config.arr_dep_ratio * 100)}
                                    fromDacams={dacamsOrigins.has('arrDepRatio')}
                                    onChange={v => { updateConfig({ arr_dep_ratio: +v / 100 }); markManual('arrDepRatio'); }}
                                    isDark={isDark}
                                />
                            </div>
                            <div className={`mt-3 pt-2 border-t border-[var(--border)] text-[11px] ${textSubDetail}`}>
                                Parámetros aplicados a todas las pistas
                            </div>
                        </div>

                        {/* Aircraft Mix (RECAT-EU, wake) — read-only, from block7.rrc_fleet_mix.
                            NOT the apron's ICAO ARC mix: same letters, different taxonomy. */}
                        <div className={`p-5 pr-7 rounded-xl border ${card}`}>
                            <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${accent}`}>
                                <BarChart3 className="w-4 h-4" /> Aircraft Mix (RECAT-EU — estela)
                                {dacamsFleetMix && <SourceBadge fromDacams={true} />}
                            </h3>
                            <AircraftMixReadOnly mix={mix} hasDacamsMix={!!dacamsFleetMix} faaMI={faaMI} dacamsAM={dacamsAM} stats={dacamsFleetMixStats} textSubDetail={textSubDetail} text={text} accent={accent} language={language} />
                        </div>

                        {/* Layer 2 + Tri-Node (pre-populated from DACAMS) */}
                        <div className={`p-5 rounded-xl border ${card}`}>
                            <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${accent}`}>
                                <Activity className="w-4 h-4" /> DACAMS Trims (Layer 2)
                            </h3>
                            <div className="space-y-3">
                                <DacamsBarRow
                                    label="NSS — Saturación Sector SDP"
                                    fromDacams={dacamsOrigins.has('congestion')}
                                    displayValue={`${(congestion * 100).toFixed(0)}%`}
                                    fillPct={Math.min(100, (congestion / 0.25) * 100)}
                                    barColorClass="bg-amber-500"
                                    textSubDetail={textSubDetail}
                                    text={text}
                                    language={language}
                                />
                                <DacamsBarRow
                                    label="WCF Score (METAR)"
                                    fromDacams={dacamsOrigins.has('wcf')}
                                    displayValue={wcfScore.toFixed(2)}
                                    fillPct={Math.min(100, wcfScore * 100)}
                                    barColorClass="bg-sky-500"
                                    textSubDetail={textSubDetail}
                                    text={text}
                                    language={language}
                                />
                                <div>
                                    <div className="flex justify-between mb-1">
                                        <span className={`text-[11px] ${textSubDetail}`}>β_w Sensitivity</span>
                                        <span className={`text-[11px] font-mono ${text}`}>{betaW.toFixed(2)}</span>
                                    </div>
                                    <input type="range" min={0} max={1} step={0.05} value={betaW}
                                        onChange={e => setBetaW(+e.target.value)}
                                        className="w-full h-1.5 accent-purple-500" />
                                </div>
                            </div>
                            <h3 className={`text-sm font-bold mt-4 mb-2 flex items-center gap-2 ${accent}`}>
                                <Building2 className="w-4 h-4" /> Tri-Node Inputs
                            </h3>
                            <div className="space-y-2">
                                <DacamsMetricRow label="RAC_eff (mov/h)" value={racEff} fromDacams={dacamsOrigins.has('racEff')} textSubDetail={textSubDetail} text={text} language={language} />
                                <DacamsMetricRow label="PTBC (mov/h)" value={ptbc} fromDacams={dacamsOrigins.has('ptbc')} textSubDetail={textSubDetail} text={text} language={language} />
                                <DacamsMetricRow label="Demand (mov/h)" value={demand} fromDacams={dacamsOrigins.has('demand')} textSubDetail={textSubDetail} text={text} language={language} />
                            </div>
                        </div>
                    </div>
                ) : (
                    /* ── SINGLE-RUNWAY MODE: 4 panels (original layout) ── */
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">

                        {/* Runway Config */}
                        <div className={`p-5 rounded-xl border ${card}`}>
                            <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${accent}`}>
                                <Plane className="w-4 h-4" /> Runway Config
                            </h3>
                            <div className="space-y-2">
                                <InputRow label="Runway ID" value={config.runway_id} onChange={v => updateConfig({ runway_id: v })} isDark={isDark} />
                                <InputRow label="Length (m)" value={config.length_m} type="number" onChange={v => updateConfig({ length_m: +v })} isDark={isDark} />
                                <SelectRow label="Mode" value={config.mode} options={MODE_LABELS} onChange={v => updateConfig({ mode: v as RunwayMode })} isDark={isDark} />
                                <SelectRow label="Weather" value={config.weather} options={WEATHER_LABELS} onChange={v => updateConfig({ weather: v as WeatherCategory })} isDark={isDark} />
                                <SelectRow label="Separation" value={config.separation_std} options={{ 'ICAO': 'ICAO', 'RECAT-EU': 'RECAT-EU', 'RECAT-PWS': 'RECAT-PWS' }} onChange={v => updateConfig({ separation_std: v as SeparationStandard })} isDark={isDark} />
                            </div>
                        </div>

                        {/* Infrastructure */}
                        <div className={`p-5 rounded-xl border ${card}`}>
                            <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${accent}`}>
                                <Settings2 className="w-4 h-4" /> Infrastructure
                            </h3>
                            <div className="space-y-2">
                                <InputRow label="RETs" value={config.n_rets} type="number" onChange={v => updateConfig({ n_rets: +v })} isDark={isDark} />
                                <InputRow label="Holding Pts" value={config.n_holding_points} type="number" onChange={v => updateConfig({ n_holding_points: +v })} isDark={isDark} />
                                <InputRow label="ILS CAT" value={config.ils_category} onChange={v => updateConfig({ ils_category: v })} isDark={isDark} />
                                <SourceInputRow
                                    label="% Llegadas"
                                    value={Math.round(config.arr_dep_ratio * 100)}
                                    fromDacams={dacamsOrigins.has('arrDepRatio')}
                                    onChange={v => { updateConfig({ arr_dep_ratio: +v / 100 }); markManual('arrDepRatio'); }}
                                    isDark={isDark}
                                />
                                <InputRow label="ROT Arr (s)" value={config.rot_arrival_s} type="number" onChange={v => updateConfig({ rot_arrival_s: +v })} isDark={isDark} />
                                <InputRow label="ROT Dep (s)" value={config.rot_departure_s} type="number" onChange={v => updateConfig({ rot_departure_s: +v })} isDark={isDark} />
                            </div>
                        </div>

                        {/* Aircraft Mix (RECAT-EU, wake) — read-only, from block7.rrc_fleet_mix.
                            NOT the apron's ICAO ARC mix: same letters, different taxonomy. */}
                        <div className={`p-5 pr-7 rounded-xl border ${card}`}>
                            <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${accent}`}>
                                <BarChart3 className="w-4 h-4" /> Aircraft Mix (RECAT-EU — estela)
                                {dacamsFleetMix && <SourceBadge fromDacams={true} />}
                            </h3>
                            <AircraftMixReadOnly mix={mix} hasDacamsMix={!!dacamsFleetMix} faaMI={faaMI} dacamsAM={dacamsAM} stats={dacamsFleetMixStats} textSubDetail={textSubDetail} text={text} accent={accent} language={language} />
                        </div>

                        {/* Layer 2: DACAMS Trims */}
                        <div className={`p-5 rounded-xl border ${card}`}>
                            <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${accent}`}>
                                <Activity className="w-4 h-4" /> DACAMS Trims (Layer 2)
                            </h3>
                            <div className="space-y-3">
                                <DacamsBarRow
                                    label="NSS — Saturación Sector SDP"
                                    fromDacams={dacamsOrigins.has('congestion')}
                                    displayValue={`${(congestion * 100).toFixed(0)}%`}
                                    fillPct={Math.min(100, (congestion / 0.25) * 100)}
                                    barColorClass="bg-amber-500"
                                    textSubDetail={textSubDetail}
                                    text={text}
                                    language={language}
                                />
                                <DacamsBarRow
                                    label="WCF Score (METAR)"
                                    fromDacams={dacamsOrigins.has('wcf')}
                                    displayValue={wcfScore.toFixed(2)}
                                    fillPct={Math.min(100, wcfScore * 100)}
                                    barColorClass="bg-sky-500"
                                    textSubDetail={textSubDetail}
                                    text={text}
                                    language={language}
                                />
                                <div>
                                    <div className="flex justify-between mb-1">
                                        <span className={`text-[11px] ${textSubDetail}`}>β_w Sensitivity</span>
                                        <span className={`text-[11px] font-mono ${text}`}>{betaW.toFixed(2)}</span>
                                    </div>
                                    <input type="range" min={0} max={1} step={0.05} value={betaW}
                                        onChange={e => setBetaW(+e.target.value)}
                                        className="w-full h-1.5 accent-purple-500" />
                                </div>
                            </div>
                            <h3 className={`text-sm font-bold mt-4 mb-2 flex items-center gap-2 ${accent}`}>
                                <Building2 className="w-4 h-4" /> Tri-Node Inputs
                            </h3>
                            <div className="space-y-2">
                                <DacamsMetricRow label="RAC_eff (mov/h)" value={racEff} fromDacams={dacamsOrigins.has('racEff')} textSubDetail={textSubDetail} text={text} language={language} />
                                <DacamsMetricRow label="PTBC (mov/h)" value={ptbc} fromDacams={dacamsOrigins.has('ptbc')} textSubDetail={textSubDetail} text={text} language={language} />
                                <DacamsMetricRow label="Demand (mov/h)" value={demand} fromDacams={dacamsOrigins.has('demand')} textSubDetail={textSubDetail} text={text} language={language} />
                            </div>
                        </div>
                    </div>
                )}

                {/* ── RESULTS: MULTI-RUNWAY ────────────── */}
                {projectRunways.length > 1 && runwayResults.some(r => r !== null) && (
                    <div className="mb-4">
                        <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 text-emerald-400`}>
                            <Gauge className="w-4 h-4" /> Capacidad por Pista Física — Layer 1 + Layer 2
                        </h3>
                        <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${projectRunways.length}, minmax(0, 1fr))` }}>
                            {projectRunways.map((rw, idx) => {
                                const res = runwayResults[idx];
                                const surfaceLabel = rw.surface || '—';
                                const ilsLabel = rw.ils_category ? `ILS ${rw.ils_category}` : 'Sin ILS';
                                const hasRET = (rw.rapid_exit_taxiways ?? 0) > 0;
                                return (
                                    <div key={idx} className={`p-5 rounded-xl border ${card}`}>
                                        {/* Header */}
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <span className={`text-lg font-bold font-mono ${accent}`}>{rw.runway_id}</span>
                                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                    {rw.length_m && (
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-[var(--surface-raised)] text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                                                            {rw.length_m}m
                                                        </span>
                                                    )}
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-[var(--surface-raised)] text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                                                        {surfaceLabel}
                                                    </span>
                                                    {hasRET ? (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-bold">RET</span>
                                                    ) : (
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-[var(--surface-raised)] text-gray-500' : 'bg-gray-50 text-gray-400'}`}>Sin RET</span>
                                                    )}
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-[var(--surface-raised)] text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                                                        {ilsLabel}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Per-runway editable ROTs */}
                                        <div className={`grid grid-cols-2 gap-2 mb-3 p-2 rounded-lg ${isDark ? 'bg-[var(--surface-raised)]' : 'bg-gray-50'}`}>
                                            <div>
                                                <span className={`text-[10px] ${textSubDetail}`}>ROT Arr (s)</span>
                                                <input type="number" value={rw.rot_arrival_s ?? 50}
                                                    onChange={e => {
                                                        const updated = [...projectRunways];
                                                        updated[idx] = { ...updated[idx], rot_arrival_s: +e.target.value };
                                                        setProjectRunways(updated);
                                                    }}
                                                    className={`w-full mt-0.5 px-2 py-1 rounded text-[11px] border ${isDark ? 'bg-[#1E293B] text-gray-200 border-[var(--border)]' : 'bg-white text-gray-800 border-gray-300'}`}
                                                />
                                            </div>
                                            <div>
                                                <span className={`text-[10px] ${textSubDetail}`}>ROT Dep (s)</span>
                                                <input type="number" value={rw.rot_departure_s ?? 40}
                                                    onChange={e => {
                                                        const updated = [...projectRunways];
                                                        updated[idx] = { ...updated[idx], rot_departure_s: +e.target.value };
                                                        setProjectRunways(updated);
                                                    }}
                                                    className={`w-full mt-0.5 px-2 py-1 rounded text-[11px] border ${isDark ? 'bg-[#1E293B] text-gray-200 border-[var(--border)]' : 'bg-white text-gray-800 border-gray-300'}`}
                                                />
                                            </div>
                                        </div>

                                        {/* Results */}
                                        {res ? (
                                            <div className="space-y-2">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <StatCard label="ARR/h" value={res.max_arrivals_per_hour} color="#22c55e" isDark={isDark} />
                                                    <StatCard label="DEP/h" value={res.max_departures_per_hour} color="#3b82f6" isDark={isDark} />
                                                    <StatCard label="Mixed/h" value={res.max_mixed_per_hour} color="#f59e0b" isDark={isDark} />
                                                    <StatCard label="Practical" value={res.practical_capacity_per_hour} color="#F26430" isDark={isDark} />
                                                </div>
                                                {res.dacams_rrc !== null && (
                                                    <div className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-emerald-900/20 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
                                                        <span className={`text-[11px] font-bold ${textSubDetail}`}>RRC (Real Runway Capacity)</span>
                                                        <span className={`text-base font-bold ${accent}`}>{res.dacams_rrc} mov/h</span>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className={`text-center py-6 text-[11px] ${textSubDetail}`}>
                                                {language === 'es' ? 'Pulsa Analizar para calcular' : 'Press Analyze to calculate'}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ── RESULTS: SINGLE RUNWAY ────────────── */}
                {(projectRunways.length <= 1) && result && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                        {/* Layer 1 Results */}
                        <div className={`p-5 rounded-xl border ${card}`}>
                            <h3 className={`text-sm font-bold mb-4 flex items-center gap-2 text-emerald-400`}>
                                <Gauge className="w-4 h-4" /> Layer 1 — {METHOD_LABELS[result.method_used as CalculationMethod]?.name || result.method_used}
                            </h3>

                            {/* Capacity Gauges */}
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <StatCard label="Arrivals/h" value={result.max_arrivals_per_hour} color="#22c55e" isDark={isDark} />
                                <StatCard label="Departures/h" value={result.max_departures_per_hour} color="#3b82f6" isDark={isDark} />
                                <StatCard label="Mixed/h" value={result.max_mixed_per_hour} color="#f59e0b" isDark={isDark} />
                                <StatCard label="Practical/h" value={result.practical_capacity_per_hour} color="#F26430" isDark={isDark} />
                            </div>

                            {/* Method-specific details */}
                            {result.faa_baseline && (
                                <div className={`p-3 rounded-lg ${'bg-[var(--surface-raised)]'} mb-3`}>
                                    <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${textSubDetail}`}>FAA Details</div>
                                    <div className="grid grid-cols-2 gap-1 text-[11px]">
                                        <span className={textSubDetail}>C_base</span><span className={`font-mono ${text}`}>{result.faa_baseline.c_base}</span>
                                        <span className={textSubDetail}>Mix Index</span><span className={`font-mono ${text}`}>{result.faa_baseline.mix_index}%</span>
                                        <span className={textSubDetail}>E (Exit)</span><span className={`font-mono ${text}`}>{result.faa_baseline.exit_factor}</span>
                                        <span className={textSubDetail}>T (T&G)</span><span className={`font-mono ${text}`}>{result.faa_baseline.touch_factor}</span>
                                        <span className={textSubDetail}>HCR</span><span className={`font-mono font-bold ${accent}`}>{result.faa_baseline.hcr} mov/h</span>
                                    </div>
                                </div>
                            )}
                            {result.sam_result && (
                                <div className={`p-3 rounded-lg ${'bg-[var(--surface-raised)]'} mb-3`}>
                                    <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${textSubDetail}`}>SAM/MTTS Details</div>
                                    <div className="grid grid-cols-2 gap-1 text-[11px]">
                                        <span className={textSubDetail}>TS</span><span className={`font-mono ${text}`}>{result.sam_result.total_separation_nm} NM</span>
                                        <span className={textSubDetail}>MV</span><span className={`font-mono ${text}`}>{result.sam_result.mean_speed_kt} kt</span>
                                        <span className={textSubDetail}>MTTS</span><span className={`font-mono ${text}`}>{result.sam_result.mtts_s}s</span>
                                        <span className={textSubDetail}>NLDG</span><span className={`font-mono ${text}`}>{result.sam_result.nldg}</span>
                                        <span className={textSubDetail}>TRC</span><span className={`font-mono font-bold ${accent}`}>{result.sam_result.trc} mov/h</span>
                                    </div>
                                </div>
                            )}

                            <div className={`text-[11px] ${textSubDetail}`}>
                                Annual: {(result.annual_capacity_estimate / 1000).toFixed(0)}k mov · Sustainable: {result.sustainable_capacity_per_hour} mov/h
                            </div>
                        </div>

                        {/* Layer 2 Results */}
                        <div className={`p-5 rounded-xl border ${card}`}>
                            <h3 className={`text-sm font-bold mb-4 flex items-center gap-2 text-amber-400`}>
                                <Activity className="w-4 h-4" /> Layer 2 — DACAMS Trims
                            </h3>

                            {result.dacams_trims && (
                                <>
                                    <div className="space-y-4 mb-4">
                                        {/* DACAMS AM */}
                                        <div className={`p-3 rounded-lg ${'bg-[var(--surface-raised)]'}`}>
                                            <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${textSubDetail}`}>DACAMS Aircraft Mix (AM)</div>
                                            <div className={`text-2xl font-bold ${accent}`}>{result.dacams_trims.dacams_am}</div>
                                            <div className={`text-[10px] ${textSubDetail}`}>AP-CAM Annex B · L=0.8 M=1.0 H=1.5 S=2.5</div>
                                        </div>

                                        {/* Congestion trim */}
                                        <div>
                                            <div className="flex justify-between text-[11px] mb-1">
                                                <span className={textSubDetail}>After Congestion Trim</span>
                                                <span className={`font-mono font-bold ${text}`}>{result.dacams_trims.capacity_after_congestion} mov/h</span>
                                            </div>
                                            <div className="w-full h-2 rounded-full bg-gray-700/50 overflow-hidden">
                                                <div className="h-full bg-amber-500 rounded-full transition-all" style={{
                                                    width: `${result.practical_capacity_per_hour > 0 ? (result.dacams_trims.capacity_after_congestion / result.practical_capacity_per_hour) * 100 : 100}%`
                                                }} />
                                            </div>
                                            <div className={`text-[10px] ${textSubDetail} mt-0.5`}>NSS={result.dacams_trims.congestion_factor.toFixed(3)} · -{(result.dacams_trims.congestion_factor * 100).toFixed(0)}% SDP</div>
                                        </div>

                                        {/* WCF trim */}
                                        <div>
                                            <div className="flex justify-between text-[11px] mb-1">
                                                <span className={textSubDetail}>After WCF Trim (RRC)</span>
                                                <span className={`font-mono font-bold ${accent}`}>{result.dacams_trims.capacity_after_wcf} mov/h</span>
                                            </div>
                                            <div className="w-full h-2 rounded-full bg-gray-700/50 overflow-hidden">
                                                <div className="h-full bg-sky-500 rounded-full transition-all" style={{
                                                    width: `${result.practical_capacity_per_hour > 0 ? (result.dacams_trims.capacity_after_wcf / result.practical_capacity_per_hour) * 100 : 100}%`
                                                }} />
                                            </div>
                                            <div className={`text-[10px] ${textSubDetail} mt-0.5`}>WCF={result.dacams_trims.wcf_score} · β={result.dacams_trims.beta_w}</div>
                                        </div>
                                    </div>

                                    {/* Declared vs RRC comparison */}
                                    <div className={`p-3 rounded-lg border ${isDark ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="text-center">
                                                <div className={`text-[10px] ${textSubDetail}`}>Declared</div>
                                                <div className={`text-lg font-bold ${text}`}>{result.practical_capacity_per_hour}</div>
                                            </div>
                                            <ArrowRight className={`w-5 h-5 ${textSubDetail}`} />
                                            <div className="text-center">
                                                <div className={`text-[10px] ${textSubDetail}`}>RRC (Real)</div>
                                                <div className={`text-lg font-bold ${accent}`}>{result.dacams_rrc}</div>
                                            </div>
                                            <div className="text-center">
                                                <div className={`text-[10px] ${textSubDetail}`}>Delta</div>
                                                <div className={`text-lg font-bold ${result.dacams_rrc! < result.practical_capacity_per_hour ? 'text-red-400' : 'text-emerald-400'}`}>
                                                    {result.dacams_rrc! - result.practical_capacity_per_hour}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Layer 3: Tri-Node Equilibrium */}
                        <div className={`p-5 rounded-xl border ${card}`}>
                            <h3 className={`text-sm font-bold mb-4 flex items-center gap-2 text-purple-400`}>
                                <Radio className="w-4 h-4" /> Layer 3 — Tri-Node Equilibrium
                            </h3>

                            {triNode ? (
                                <>
                                    {/* Traffic Light Nodes */}
                                    <div className="flex items-center justify-center gap-2 mb-4">
                                        <TriNodeBlock
                                            label="RUNWAY" value={triNode.inputs.rrc}
                                            isBottleneck={triNode.bottleneck_node === 'RUNWAY'}
                                            waste={triNode.mismatch_vector.RRC_waste}
                                            isDark={isDark}
                                        />
                                        <ArrowRight className={`w-4 h-4 ${textSubDetail}`} />
                                        <TriNodeBlock
                                            label="APRON" value={triNode.inputs.rac_eff}
                                            isBottleneck={triNode.bottleneck_node === 'APRON'}
                                            waste={triNode.mismatch_vector.RAC_waste}
                                            isDark={isDark}
                                        />
                                        <ArrowRight className={`w-4 h-4 ${textSubDetail}`} />
                                        <TriNodeBlock
                                            label="TERMINAL" value={triNode.inputs.ptbc}
                                            isBottleneck={triNode.bottleneck_node === 'TERMINAL'}
                                            waste={triNode.mismatch_vector.PTBC_waste}
                                            isDark={isDark}
                                        />
                                    </div>

                                    {/* CC & BCI */}
                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        <div className={`p-3 rounded-lg text-center ${'bg-[var(--surface-raised)]'}`}>
                                            <div className={`text-[10px] uppercase ${textSubDetail}`}>CC (Controlling)</div>
                                            <div className={`text-2xl font-bold ${accent}`}>{triNode.controlling_capacity}</div>
                                            <div className={`text-[10px] ${textSubDetail}`}>mov/h</div>
                                        </div>
                                        <div className={`p-3 rounded-lg text-center ${'bg-[var(--surface-raised)]'}`}>
                                            <div className={`text-[10px] uppercase ${textSubDetail}`}>BCI</div>
                                            <div className={`text-2xl font-bold ${text}`}>{(triNode.balanced_capacity_index * 100).toFixed(0)}%</div>
                                            <div className={`text-[10px] ${textSubDetail}`}>Balance Index</div>
                                        </div>
                                    </div>

                                    {/* RGPP Gauge */}
                                    <div className={`p-3 rounded-lg mb-3 ${triNode.rgpp_alert
                                        ? (isDark ? 'bg-red-900/30 border border-red-500/50' : 'bg-red-50 border border-red-200')
                                        : (isDark ? 'bg-emerald-900/20 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200')
                                        }`}>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className={`text-[10px] uppercase font-bold ${textSubDetail}`}>RGPP (RAC/RRC)</div>
                                                <div className={`text-xl font-bold ${triNode.rgpp_alert ? 'text-red-400' : 'text-emerald-400'}`}>
                                                    {triNode.rgpp.toFixed(2)}
                                                </div>
                                            </div>
                                            {triNode.rgpp_alert && (
                                                <div className="text-red-400 text-[10px] font-medium text-right">
                                                    <AlertTriangle className="w-4 h-4 mb-0.5 inline" /><br />
                                                    {language === 'es' ? 'Pista inyecta más' : 'Runway injects more'}<br />
                                                    {language === 'es' ? 'de lo que absorbe' : 'than absorbed'}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* SVS */}
                                    <div className={`p-3 rounded-lg ${triNode.svs_alert
                                        ? (isDark ? 'bg-red-900/30 border border-red-500/50' : 'bg-red-50 border border-red-200')
                                        : ('bg-[var(--surface-raised)]')
                                        }`}>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className={`text-[10px] uppercase font-bold ${textSubDetail}`}>SVS (Slot Viability)</div>
                                                <div className={`text-xl font-bold ${triNode.svs_alert ? 'text-red-400' : 'text-emerald-400'}`}>
                                                    {(triNode.slot_viability_score * 100).toFixed(1)}%
                                                </div>
                                            </div>
                                            {triNode.svs_alert && (
                                                <div className="px-2 py-1 rounded bg-red-500/20 text-red-300 text-[10px] font-bold">
                                                    FLOW CONTROL
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className={`text-center py-8 ${textSubDetail} text-sm`}>
                                    {language === 'es' ? 'Ejecuta el análisis para ver resultados Tri-Node' : 'Run analysis to see Tri-Node results'}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Layer 3: Tri-Node (multi-runway mode) ── */}
                {projectRunways.length > 1 && triNode && (
                    <div className={`mt-4 p-5 rounded-xl border ${card}`}>
                        <h3 className={`text-sm font-bold mb-4 flex items-center gap-2 text-purple-400`}>
                            <Radio className="w-4 h-4" /> Layer 3 — Tri-Node Equilibrium
                            <span className={`ml-2 text-[10px] font-normal ${textSubDetail}`}>(RRC = mínimo entre pistas)</span>
                        </h3>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Node diagram */}
                            <div>
                                <div className="flex items-center justify-center gap-3 mb-4">
                                    <TriNodeBlock
                                        label="RUNWAY" value={triNode.inputs.rrc}
                                        isBottleneck={triNode.bottleneck_node === 'RUNWAY'}
                                        waste={triNode.mismatch_vector.RRC_waste}
                                        isDark={isDark}
                                    />
                                    <ArrowRight className={`w-4 h-4 ${textSubDetail}`} />
                                    <TriNodeBlock
                                        label="APRON" value={triNode.inputs.rac_eff}
                                        isBottleneck={triNode.bottleneck_node === 'APRON'}
                                        waste={triNode.mismatch_vector.RAC_waste}
                                        isDark={isDark}
                                    />
                                    <ArrowRight className={`w-4 h-4 ${textSubDetail}`} />
                                    <TriNodeBlock
                                        label="TERMINAL" value={triNode.inputs.ptbc}
                                        isBottleneck={triNode.bottleneck_node === 'TERMINAL'}
                                        waste={triNode.mismatch_vector.PTBC_waste}
                                        isDark={isDark}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className={`p-3 rounded-lg text-center bg-[var(--surface-raised)]`}>
                                        <div className={`text-[10px] uppercase ${textSubDetail}`}>CC (Controlling)</div>
                                        <div className={`text-2xl font-bold ${accent}`}>{triNode.controlling_capacity}</div>
                                        <div className={`text-[10px] ${textSubDetail}`}>mov/h</div>
                                    </div>
                                    <div className={`p-3 rounded-lg text-center bg-[var(--surface-raised)]`}>
                                        <div className={`text-[10px] uppercase ${textSubDetail}`}>BCI</div>
                                        <div className={`text-2xl font-bold ${text}`}>{(triNode.balanced_capacity_index * 100).toFixed(0)}%</div>
                                        <div className={`text-[10px] ${textSubDetail}`}>Balance Index</div>
                                    </div>
                                </div>
                            </div>
                            {/* RGPP + SVS */}
                            <div className="space-y-3">
                                <div className={`p-3 rounded-lg ${triNode.rgpp_alert
                                    ? (isDark ? 'bg-red-900/30 border border-red-500/50' : 'bg-red-50 border border-red-200')
                                    : (isDark ? 'bg-emerald-900/20 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200')
                                    }`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className={`text-[10px] uppercase font-bold ${textSubDetail}`}>RGPP (RAC/RRC)</div>
                                            <div className={`text-xl font-bold ${triNode.rgpp_alert ? 'text-red-400' : 'text-emerald-400'}`}>
                                                {triNode.rgpp.toFixed(2)}
                                            </div>
                                        </div>
                                        {triNode.rgpp_alert && (
                                            <div className="text-red-400 text-[10px] font-medium text-right">
                                                <AlertTriangle className="w-4 h-4 mb-0.5 inline" /><br />
                                                {language === 'es' ? 'Pista inyecta más de lo que absorbe' : 'Runway injects more than absorbed'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className={`p-3 rounded-lg ${triNode.svs_alert
                                    ? (isDark ? 'bg-red-900/30 border border-red-500/50' : 'bg-red-50 border border-red-200')
                                    : 'bg-[var(--surface-raised)]'
                                    }`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className={`text-[10px] uppercase font-bold ${textSubDetail}`}>SVS (Slot Viability)</div>
                                            <div className={`text-xl font-bold ${triNode.svs_alert ? 'text-red-400' : 'text-emerald-400'}`}>
                                                {(triNode.slot_viability_score * 100).toFixed(1)}%
                                            </div>
                                        </div>
                                        {triNode.svs_alert && (
                                            <div className="px-2 py-1 rounded bg-red-500/20 text-red-300 text-[10px] font-bold">
                                                FLOW CONTROL
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Notes (single-runway mode) */}
                {result && result.notes.length > 0 && (
                    <div className={`mt-4 p-4 rounded-xl border ${card}`}>
                        <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className={`text-sm font-bold flex items-center gap-2 ${textSubDetail} hover:${text}`}
                        >
                            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            Calculation Notes ({result.notes.length})
                        </button>
                        {showAdvanced && (
                            <ul className={`mt-2 space-y-1 text-[11px] ${textSubDetail}`}>
                                {result.notes.map((n, i) => <li key={i}>• {n}</li>)}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ==================== Sub-Components ====================

/** Small inline badge: green "DACAMS" when value came from analysis, gray "Manual" when user-edited */
const SourceBadge: React.FC<{ fromDacams: boolean }> = ({ fromDacams }) => (
    fromDacams
        ? <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-sm bg-emerald-500/20 text-emerald-400 font-bold tracking-wide align-middle">DACAMS</span>
        : <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-sm bg-gray-500/20 text-gray-400 font-bold tracking-wide align-middle">Manual</span>
);

/** The project's single runway capacity, as resolved by the DACAMS tri-node (block 7).
 *  System-level: when the airport runs dependent parallel arrivals this figure already
 *  covers both runways, so it is never multiplied or summed per runway. */
const DacamsRrcCard: React.FC<{
    data: { rrc: number; source: string; detail: string; method: string; breakdown: any | null; notes: string[] } | null;
    card: string;
    text: string;
    accent: string;
    textSubDetail: string;
    language: string;
}> = ({ data, card, text, accent, textSubDetail, language }) => {
    const es = language === 'es';
    if (!data) {
        return (
            <div className={`mb-4 p-4 rounded-xl border ${card} text-[11px] ${textSubDetail}`}>
                {es
                    ? 'Sin capacidad de pista DACAMS para este proyecto — ejecuta el análisis para generar el bloque 7.'
                    : 'No DACAMS runway capacity for this project — run the analysis to produce block 7.'}
            </div>
        );
    }
    const b = data.breakdown;
    const modelled = data.source === 'runcap';
    return (
        <div className={`mb-4 p-5 rounded-xl border ${card}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <h3 className={`text-sm font-bold flex items-center gap-2 ${accent}`}>
                    <Zap className="w-4 h-4" />
                    {es ? 'effRRC DACAMS — sistema de pistas' : 'DACAMS effRRC — runway system'}
                </h3>
                <span
                    className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold tracking-wide ${
                        modelled
                            ? 'bg-[#F26430]/20 text-[#F26430]'
                            : 'bg-amber-500/20 text-amber-400'
                    }`}
                >
                    {modelled ? `RunCap ${data.method}` : data.source}
                </span>
            </div>
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                <div>
                    <span className={`text-3xl font-bold font-mono ${text}`}>{data.rrc.toFixed(1)}</span>
                    <span className={`ml-1 text-[11px] ${textSubDetail}`}>mov/h</span>
                </div>
                {b && (
                    <div className={`text-[11px] font-mono ${textSubDetail}`}>
                        ARR {b.max_arrivals_per_hour} · DEP {b.max_departures_per_hour} · MIX{' '}
                        {b.max_mixed_per_hour} · {es ? 'sep. llegada' : 'arr sep'}{' '}
                        {b.avg_arrival_separation_s}s
                    </div>
                )}
            </div>
            {data.detail && <div className={`mt-2 text-[11px] ${textSubDetail}`}>{data.detail}</div>}
            {data.notes.length > 0 && (
                <ul className={`mt-2 space-y-0.5 text-[11px] ${textSubDetail}`}>
                    {data.notes.map((n, i) => (
                        <li key={i}>· {n}</li>
                    ))}
                </ul>
            )}
            <div className={`mt-3 pt-2 border-t border-[var(--border)] text-[10px] ${textSubDetail}`}>
                {es
                    ? 'Cifra de sistema (todas las pistas juntas): no se suma por pista. Fuente única del nodo pista.'
                    : 'System-level figure (all runways together): never summed per runway. Single source for the runway node.'}
            </div>
        </div>
    );
};

/** Read-only RECAT-EU Aircraft Mix — automatic from block7.rrc_fleet_mix, never user-editable.
 *  RECAT-EU classifies by WAKE TURBULENCE (an A320 is D). It is not the apron's ICAO ARC
 *  taxonomy, which classifies by wingspan (the same A320 is C) — see the AP-CAM page.
 *  If no DACAMS mix key was found for the project, shows an honest "no data" state
 *  instead of the percentage bars (REGLA DE ORO: never present a fabricated default as data). */
const AircraftMixReadOnly: React.FC<{
    mix: AircraftMix;
    hasDacamsMix: boolean;
    faaMI: string;
    dacamsAM: string;
    stats?: any | null;
    textSubDetail: string;
    text: string;
    accent: string;
    language: string;
}> = ({ mix, hasDacamsMix, faaMI, dacamsAM, stats, textSubDetail, text, accent, language }) => {
    if (!hasDacamsMix) {
        return (
            <div className={`text-center py-6 text-[11px] ${textSubDetail}`}>
                {language === 'es' ? 'Sin dato de mezcla DACAMS' : 'No DACAMS mix data'}
            </div>
        );
    }
    return (
        <>
            <div className="space-y-1.5">
                {Object.entries(RECAT_EU_LABELS).map(([key, label]) => {
                    const pct = (mix as any)[key] ?? 0;
                    return (
                        <div key={key} className="flex items-center justify-between gap-3">
                            <span className={`text-[11px] ${textSubDetail} w-6 shrink-0`}>{label.split('—')[0]}</span>
                            <div className="flex-1 h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                                <div className="h-full rounded-full bg-[var(--aipron-accent)]" style={{ width: `${Math.min(100, pct)}%` }} />
                            </div>
                            <span className={`text-[11px] font-mono w-10 text-right ${text}`}>{pct.toFixed(1)}%</span>
                        </div>
                    );
                })}
            </div>
            <div className={`mt-3 pt-2 border-t border-[var(--border)]`}>
                <div className="flex justify-between text-[11px]">
                    <span className={textSubDetail}>FAA MI</span>
                    <span className={`font-mono font-bold ${text}`}>{faaMI}%</span>
                </div>
                <div className="flex justify-between text-[11px]">
                    <span className={textSubDetail}>DACAMS AM</span>
                    <span className={`font-mono font-bold ${accent}`}>{dacamsAM}</span>
                </div>
                {stats?.total > 0 && (
                    <div className={`mt-1.5 text-[10px] leading-snug ${textSubDetail}`}>
                        {language === 'es' ? 'Clasificados' : 'Classified'}{' '}
                        <span className="font-mono">
                            {stats.classified}/{stats.total}
                        </span>{' '}
                        {language === 'es' ? 'movimientos' : 'movements'}
                        {stats.unknown > 0 && (
                            <>
                                {' · '}
                                {(stats.unknown_share * 100).toFixed(1)}%{' '}
                                {language === 'es' ? 'sin categoría RECAT' : 'without a RECAT category'}
                                {stats.unknown_types?.length > 0 &&
                                    ` (${stats.unknown_types.slice(0, 4).join(', ')})`}
                            </>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

/** Read-only Layer 2 trim bar (NSS congestion / WCF score) — DACAMS-seeded, no editing.
 *  Shows "sin dato" instead of the bar when the value never came from analysis_results. */
const DacamsBarRow: React.FC<{
    label: string;
    fromDacams: boolean;
    displayValue: string;
    fillPct: number;
    barColorClass: string;
    textSubDetail: string;
    text: string;
    language: string;
}> = ({ label, fromDacams, displayValue, fillPct, barColorClass, textSubDetail, text, language }) => (
    <div>
        <div className="flex justify-between mb-1">
            <span className={`text-[11px] ${textSubDetail}`}>
                {label}
                <SourceBadge fromDacams={fromDacams} />
            </span>
            <span className={`text-[11px] font-mono ${text}`}>
                {fromDacams ? displayValue : (language === 'es' ? 'sin dato' : 'no data')}
            </span>
        </div>
        <div className="w-full h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
            <div className={`h-full rounded-full ${barColorClass}`} style={{ width: `${fromDacams ? fillPct : 0}%` }} />
        </div>
    </div>
);

/** Read-only Tri-Node metric (RAC_eff / PTBC / Demand) — DACAMS-seeded, no editing. */
const DacamsMetricRow: React.FC<{
    label: string;
    value: number;
    fromDacams: boolean;
    textSubDetail: string;
    text: string;
    language: string;
}> = ({ label, value, fromDacams, textSubDetail, text, language }) => (
    <div className="flex items-center justify-between gap-2">
        <span className={`text-[11px] ${textSubDetail} w-32 shrink-0 flex items-center flex-wrap gap-1`}>
            {label}
            <SourceBadge fromDacams={fromDacams} />
        </span>
        <span className={`text-[12px] font-mono font-semibold ${text}`}>
            {fromDacams ? value : (language === 'es' ? 'sin dato' : 'no data')}
        </span>
    </div>
);

const InputRow: React.FC<{ label: string; value: any; type?: string; onChange: (v: string) => void; isDark: boolean }> = ({ label, value, type = 'text', onChange, isDark }) => (
    <div className="flex items-center justify-between gap-2">
        <span className={`text-[11px] ${'text-[var(--text-secondary)]'} w-24 shrink-0`}>{label}</span>
        <input
            type={type} value={value}
            onChange={e => onChange(e.target.value)}
            className={`w-full px-2 py-1 rounded text-[12px] border ${isDark ? 'bg-[#1E293B] text-gray-200 border-[var(--border)]' : 'bg-white text-gray-800 border-gray-300'}`}
        />
    </div>
);

/** InputRow variant that shows a DACAMS / Manual source badge inline with the label */
const SourceInputRow: React.FC<{ label: string; value: any; fromDacams: boolean; onChange: (v: string) => void; isDark: boolean }> = ({ label, value, fromDacams, onChange, isDark }) => (
    <div className="flex items-center justify-between gap-2">
        <span className={`text-[11px] ${'text-[var(--text-secondary)]'} w-32 shrink-0 flex items-center flex-wrap gap-1`}>
            {label}
            <SourceBadge fromDacams={fromDacams} />
        </span>
        <input
            type="number" value={value}
            onChange={e => onChange(e.target.value)}
            className={`w-full px-2 py-1 rounded text-[12px] border ${isDark ? 'bg-[#1E293B] text-gray-200 border-[var(--border)]' : 'bg-white text-gray-800 border-gray-300'}`}
        />
    </div>
);

const SelectRow: React.FC<{ label: string; value: string; options: Record<string, string>; onChange: (v: string) => void; isDark: boolean }> = ({ label, value, options, onChange, isDark }) => (
    <div className="flex items-center justify-between gap-2">
        <span className={`text-[11px] ${'text-[var(--text-secondary)]'} w-24 shrink-0`}>{label}</span>
        <select
            value={value}
            onChange={e => onChange(e.target.value)}
            className={`w-full px-2 py-1 rounded text-[12px] border ${isDark ? 'bg-[#1E293B] text-gray-200 border-[var(--border)]' : 'bg-white text-gray-800 border-gray-300'}`}
        >
            {Object.entries(options).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
    </div>
);

const StatCard: React.FC<{ label: string; value: number; color: string; isDark: boolean }> = ({ label, value, color, isDark }) => (
    <div className={`p-3 rounded-lg text-center ${'bg-[var(--surface-raised)]'}`}>
        <div className="text-2xl font-bold" style={{ color }}>{value}</div>
        <div className={`text-[10px] ${'text-[var(--text-secondary)]'}`}>{label}</div>
    </div>
);

const TriNodeBlock: React.FC<{ label: string; value: number; isBottleneck: boolean; waste: number; isDark: boolean }> = ({ label, value, isBottleneck, waste, isDark }) => (
    <div className={`relative p-3 rounded-lg text-center border-2 transition-all ${isBottleneck
        ? 'border-red-500 bg-red-500/10 shadow-[0_0_15px_rgba(239,68,68,0.3)] animate-pulse'
        : isDark ? 'border-[var(--border)] bg-[var(--surface-raised)]' : 'border-[var(--border)] bg-gray-50'
        }`} style={{ minWidth: 80 }}>
        <div className={`text-[9px] font-bold uppercase tracking-wider mb-1 ${isBottleneck ? 'text-red-400' : 'text-[var(--text-secondary)]'}`}>
            {label}
        </div>
        <div className={`text-lg font-bold ${isBottleneck ? 'text-red-400' : isDark ? 'text-gray-100' : 'text-gray-800'}`}>
            {value.toFixed(0)}
        </div>
        {waste > 0 && (
            <div className={`text-[9px] ${isDark ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-secondary)]'}`}>+{waste.toFixed(0)} headroom</div>
        )}
    </div>
);

export default RunCapPage;
